import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createRoute, Link, Navigate } from '@tanstack/react-router'
import {
  Bell,
  BellOff,
  Bot,
  CalendarSync,
  Check,
  ChevronDown,
  Copy,
  Download,
  Eye,
  EyeOff,
  Receipt,
  RefreshCw,
  Trash2,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { SettingsLayout } from '../components/settings-layout'
import { Button } from '../components/ui/button'
import { confirm } from '../components/ui/confirm-dialog'
import { api, type FibuModus } from '../lib/api'
import {
  getPermission,
  isIOSBrowserTab,
  type NotifPermission,
  isSupported as notificationsSupported,
} from '../lib/notifications'
import { notify } from '../lib/notify'
import { disablePush, enablePush, pushEnabled, pushSupported } from '../lib/push'
import { Route as rootRoute } from './__root'

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/integrations',
  component: IntegrationsPage,
})

export function fastbillSichtbar(modus: FibuModus, isAdmin: boolean): boolean {
  return isAdmin && modus !== 'exklusiv'
}

function IntegrationsPage() {
  const { data: meData, isLoading: meLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.me(),
  })
  const { data: fibuStatus } = useQuery({
    queryKey: ['fibu-status'],
    queryFn: () => api.fibuStatus(),
    enabled: !!meData?.user,
  })

  if (meLoading) return null
  if (!meData?.user) return <Navigate to="/login" />

  const isAdmin = meData.user.role === 'admin'

  return (
    <SettingsLayout
      title="Integrationen"
      description="Kalender-Abo, Push-Benachrichtigungen, App-Installation und weitere geräteübergreifende Funktionen."
    >
      <div className="space-y-4 max-w-2xl">
        <CalendarFeedSection />
        <TeamCalendarFeedSection user={meData.user} />
        <PushNotificationSection />
        <PWAInstallSection />
        {fibuStatus && fastbillSichtbar(fibuStatus.modus, isAdmin) && <FastbillSection />}
        {isAdmin && <AiAssistantLink />}
      </div>
    </SettingsLayout>
  )
}

/* ------------------------------------------------------------------ */
/*  Section 1: Kalender-Abo (ICS Feed)                                */
/* ------------------------------------------------------------------ */

function CollapsibleInstructions({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) {
  const [open, setOpen] = useState(false)
  return (
    <div className="rounded-lg border border-border-subtle bg-bg-overlay/30">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between px-4 py-2.5 text-sm text-text-secondary hover:text-text-primary transition-colors"
      >
        <span>{title}</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="px-4 pb-3 text-sm text-text-secondary space-y-2">{children}</div>}
    </div>
  )
}

function CalendarFeedSection() {
  const queryClient = useQueryClient()
  const [copied, setCopied] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['calendar-feed-url'],
    queryFn: () => api.getCalendarFeedUrl(),
  })

  const regenerate = useMutation({
    mutationFn: () => api.regenerateCalendarFeedToken(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-feed-url'] })
      notify.success('Neuer Feed-Token generiert. Bisherige Kalender-Abos verlieren den Zugriff.')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const onCopy = async () => {
    if (!data?.feedUrl) return
    try {
      await navigator.clipboard.writeText(data.feedUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      notify.error('Kopieren fehlgeschlagen')
    }
  }

  const onRegenerate = async () => {
    const ok = await confirm({
      title: 'Token erneuern?',
      description:
        'Bestehende Kalender-Abos in externen Apps verlieren den Zugriff. Du musst die neue URL in allen Kalender-Apps erneut eintragen.',
      confirmLabel: 'Token erneuern',
      variant: 'danger',
    })
    if (ok) regenerate.mutate()
  }

  return (
    <section className="rounded-2xl border border-border-subtle bg-bg-elevated/40 p-6 space-y-4">
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-full bg-accent/15 text-accent flex items-center justify-center shrink-0">
          <CalendarSync className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-medium">Kalender-Abo (ICS Feed)</h3>
          <p className="text-sm text-text-tertiary mt-1">
            Kopiere die URL und füge sie in deiner Kalender-App hinzu. Deine Zeiteinträge, Meetings
            und Abwesenheiten erscheinen dann automatisch im externen Kalender.
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="text-sm text-text-tertiary">Lade Feed-URL …</div>
      ) : data?.feedUrl ? (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <input
              readOnly
              value={data.feedUrl}
              className="flex-1 h-9 rounded-md border border-border-default bg-bg-overlay px-3 text-xs font-mono text-text-secondary select-all focus-visible:outline-none focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-accent/30"
              onClick={(e) => (e.target as HTMLInputElement).select()}
            />
            <Button variant="outline" size="sm" onClick={onCopy}>
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              {copied ? 'Kopiert' : 'Kopieren'}
            </Button>
          </div>

          <div className="space-y-2">
            <CollapsibleInstructions title="iPhone / iPad">
              <ol className="list-decimal list-inside space-y-0.5">
                <li>Einstellungen öffnen</li>
                <li>Kalender &rarr; Accounts &rarr; Account hinzufügen</li>
                <li>Andere &rarr; Kalenderabo hinzufügen</li>
                <li>Feed-URL einfügen und bestätigen</li>
              </ol>
            </CollapsibleInstructions>
            <CollapsibleInstructions title="Android (Google Calendar)">
              <ol className="list-decimal list-inside space-y-0.5">
                <li>Google Calendar öffnen</li>
                <li>Einstellungen &rarr; Über URL abonnieren</li>
                <li>Feed-URL einfügen und speichern</li>
              </ol>
              <p className="text-xs text-text-tertiary mt-1">
                Hinweis: Google Calendar synchronisiert ICS-Feeds nur alle paar Stunden.
              </p>
            </CollapsibleInstructions>
            <CollapsibleInstructions title="Outlook">
              <ol className="list-decimal list-inside space-y-0.5">
                <li>Kalender öffnen</li>
                <li>Kalender hinzufügen &rarr; Aus dem Internet abonnieren</li>
                <li>Feed-URL einfügen und speichern</li>
              </ol>
            </CollapsibleInstructions>
          </div>

          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={onRegenerate}
              disabled={regenerate.isPending}
            >
              <RefreshCw className="h-4 w-4" /> Token erneuern
            </Button>
            <span className="text-xs text-text-tertiary">
              Danach müssen externe Kalender-Apps die neue URL erhalten.
            </span>
          </div>
        </div>
      ) : null}
    </section>
  )
}

/* ------------------------------------------------------------------ */
/*  Section 1b: Team-Kalender-Abo (supervisors + admins only)         */
/* ------------------------------------------------------------------ */

function TeamCalendarFeedSection({ user }: { user: { id: string; role: 'admin' | 'user' } }) {
  const [copied, setCopied] = useState(false)

  // Surface this section to anyone who likely has subordinates: admins
  // always, plus we additionally check whether the user is a supervisor
  // of anyone. The query is cheap (single SELECT count) but kept simple
  // by reusing /team-status which is already used elsewhere.
  const { data: teamStatus } = useQuery({
    queryKey: ['reports', 'team-status'],
    queryFn: () => api.teamStatus(),
    staleTime: 60_000,
  })

  // Heuristic: show for admins (always) or anyone whose team-status returns
  // more than one user — that means they have visibility into others.
  const hasTeamView =
    user.role === 'admin' ||
    (!!teamStatus &&
      teamStatus.absent.length + teamStatus.active.length + teamStatus.available.length > 1)

  const { data, isLoading } = useQuery({
    queryKey: ['team-calendar-feed-url'],
    queryFn: () => api.getTeamCalendarFeedUrl(),
    enabled: hasTeamView,
  })

  const onCopy = async () => {
    if (!data?.feedUrl) return
    try {
      await navigator.clipboard.writeText(data.feedUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      notify.error('Kopieren fehlgeschlagen')
    }
  }

  if (!hasTeamView) return null

  return (
    <section className="rounded-2xl border border-border-subtle bg-bg-elevated/40 p-6 space-y-4">
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-full bg-accent/15 text-accent flex items-center justify-center shrink-0">
          <CalendarSync className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-medium">Team-Abwesenheiten (ICS Feed)</h3>
          <p className="text-sm text-text-tertiary mt-1">
            Genehmigte Abwesenheiten deiner Mitarbeiter (Admins sehen alle). Hilfreich für die
            Urlaubsplanung — einfach in den eigenen Kalender abonnieren.
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="text-sm text-text-tertiary">Lade Feed-URL …</div>
      ) : data?.feedUrl ? (
        <div className="flex items-center gap-2">
          <input
            readOnly
            value={data.feedUrl}
            className="flex-1 h-9 rounded-md border border-border-default bg-bg-overlay px-3 text-xs font-mono text-text-secondary select-all focus-visible:outline-none focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-accent/30"
            onClick={(e) => (e.target as HTMLInputElement).select()}
          />
          <Button variant="outline" size="sm" onClick={onCopy}>
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            {copied ? 'Kopiert' : 'Kopieren'}
          </Button>
        </div>
      ) : null}
    </section>
  )
}

/* ------------------------------------------------------------------ */
/*  Section 2: Push-Benachrichtigungen                                */
/* ------------------------------------------------------------------ */

function PushNotificationSection() {
  const queryClient = useQueryClient()
  const [perm, setPerm] = useState<NotifPermission>(() => getPermission())
  const [subscribed, setSubscribed] = useState(false)
  const [busy, setBusy] = useState(false)

  const { data: subsData, refetch: refetchSubs } = useQuery({
    queryKey: ['push-subscriptions'],
    queryFn: () => api.listPushSubscriptions(),
  })

  const removeSub = useMutation({
    mutationFn: (id: string) => api.unsubscribePush(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['push-subscriptions'] })
      notify.success('Gerät entfernt')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  useEffect(() => {
    const id = setInterval(() => setPerm(getPermission()), 2000)
    return () => clearInterval(id)
  }, [])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      if (!pushSupported()) return
      const en = await pushEnabled()
      if (!cancelled) setSubscribed(en)
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const onEnable = async () => {
    setBusy(true)
    try {
      const ok = await enablePush()
      setPerm(getPermission())
      setSubscribed(ok)
      if (ok) {
        await api.testPush()
        refetchSubs()
        notify.success('Benachrichtigungen aktiviert — Test-Push gesendet.')
      } else if (getPermission() === 'denied') {
        notify.error(
          'Benachrichtigungen blockiert. Im Browser musst du sie für diese Seite freigeben.',
        )
      } else {
        notify.error('Aktivierung fehlgeschlagen — siehe Konsole.')
      }
    } catch (err) {
      notify.error((err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const onDisable = async () => {
    setBusy(true)
    try {
      await disablePush()
      setSubscribed(false)
      refetchSubs()
      notify.success('Benachrichtigungen deaktiviert.')
    } catch (err) {
      notify.error((err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const onTest = async () => {
    try {
      await api.testPush()
      notify.success('Test-Push gesendet.')
    } catch (err) {
      notify.error((err as Error).message)
    }
  }

  const formatSubscriptionLabel = (sub: {
    userAgent: string | null
    createdAt: string
    lastUsedAt: string
  }) => {
    const ua = sub.userAgent ?? 'Unbekanntes Gerät'
    let label = 'Gerät'
    if (/iPhone|iPad|iPod/.test(ua)) label = 'iPhone/iPad'
    else if (/Android/.test(ua)) label = 'Android'
    else if (/Windows/.test(ua)) label = 'Windows'
    else if (/Macintosh|Mac OS/.test(ua)) label = 'Mac'
    else if (/Linux/.test(ua)) label = 'Linux'

    if (/Chrome/.test(ua) && !/Edg/.test(ua)) label += ' (Chrome)'
    else if (/Firefox/.test(ua)) label += ' (Firefox)'
    else if (/Safari/.test(ua) && !/Chrome/.test(ua)) label += ' (Safari)'
    else if (/Edg/.test(ua)) label += ' (Edge)'

    return label
  }

  return (
    <section className="rounded-2xl border border-border-subtle bg-bg-elevated/40 p-6 space-y-4">
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-full bg-accent/15 text-accent flex items-center justify-center shrink-0">
          {subscribed ? <Bell className="h-4 w-4" /> : <BellOff className="h-4 w-4" />}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-medium">Push-Benachrichtigungen</h3>
          <p className="text-sm text-text-tertiary mt-1">
            Benachrichtigungen funktionieren auf allen Geräten separat. Aktiviere Push auf jedem
            Gerät, auf dem du Benachrichtigungen erhalten möchtest.
          </p>
        </div>
      </div>

      {/* iOS browser-tab warning */}
      {isIOSBrowserTab() && (
        <div className="rounded-lg border border-state-warning/30 bg-state-warning/5 px-4 py-3 text-sm text-text-secondary space-y-2">
          <p className="font-medium text-state-warning">
            Auf iPhone/iPad nur als App vom Home-Bildschirm.
          </p>
          <p>
            Auf iPhone/iPad muss Ember zum Home-Bildschirm hinzugefügt werden, damit
            Push-Benachrichtigungen funktionieren. Safari liefert im normalen Browser-Tab keine
            Web-Notifications aus.
          </p>
        </div>
      )}

      {/* Not supported at all */}
      {!isIOSBrowserTab() && !notificationsSupported() && (
        <div className="rounded-lg border border-border-subtle bg-bg-overlay/30 px-4 py-2.5 text-sm text-text-tertiary">
          Dein Browser unterstützt keine Web-Notifications. Funktioniert in Chrome, Firefox, Edge
          und Safari ab Version 16.
        </div>
      )}

      {/* Active subscription status */}
      {subscribed && (
        <div className="rounded-lg border border-state-success/30 bg-state-success/5 px-4 py-2.5 text-sm text-state-success">
          Aktiv auf diesem Gerät — du erhältst Push-Benachrichtigungen.
        </div>
      )}

      {/* Permission denied warning */}
      {perm === 'denied' && !subscribed && !isIOSBrowserTab() && (
        <div className="rounded-lg border border-state-danger/30 bg-state-danger/5 px-4 py-2.5 text-sm text-state-danger space-y-1">
          <p className="font-medium">Im Browser blockiert.</p>
          <p className="text-text-secondary">
            Klick im Adressfeld auf das Schloss-Icon &rarr; &bdquo;Benachrichtigungen&ldquo; auf
            &bdquo;Zulassen&ldquo; stellen und die Seite neu laden.
          </p>
        </div>
      )}

      {/* Action buttons */}
      {!isIOSBrowserTab() && notificationsSupported() && (
        <div className="flex flex-wrap items-center gap-3">
          {subscribed ? (
            <>
              <Button variant="ghost" onClick={onDisable} disabled={busy}>
                <BellOff className="h-4 w-4" /> Deaktivieren
              </Button>
              <Button variant="ghost" onClick={onTest} disabled={busy}>
                Test-Push
              </Button>
            </>
          ) : (
            perm !== 'denied' && (
              <>
                <Button onClick={onEnable} disabled={busy}>
                  <Bell className="h-4 w-4" /> Auf diesem Gerät aktivieren
                </Button>
                <span className="text-xs text-text-tertiary">
                  Der Browser fragt dich einmalig um Erlaubnis.
                </span>
              </>
            )
          )}
        </div>
      )}

      {/* List of active subscriptions */}
      {subsData?.subscriptions && subsData.subscriptions.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-text-secondary">Aktive Geräte</h4>
          <div className="space-y-1.5">
            {subsData.subscriptions.map((sub) => (
              <div
                key={sub.id}
                className="flex items-center justify-between rounded-lg border border-border-subtle bg-bg-overlay/30 px-4 py-2.5"
              >
                <div className="text-sm text-text-secondary">
                  <span>{formatSubscriptionLabel(sub)}</span>
                  <span className="text-xs text-text-tertiary ml-2">
                    Zuletzt aktiv:{' '}
                    {new Date(sub.lastUsedAt).toLocaleDateString('de-DE', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                    })}
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeSub.mutate(sub.id)}
                  disabled={removeSub.isPending}
                  className="text-state-danger hover:bg-state-danger/10"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Entfernen
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  )
}

/* ------------------------------------------------------------------ */
/*  Section 3: PWA / App installieren                                 */
/* ------------------------------------------------------------------ */

function PWAInstallSection() {
  const [isInstalled, setIsInstalled] = useState(false)

  useEffect(() => {
    const check = () => {
      if (typeof window === 'undefined') return false
      return window.matchMedia('(display-mode: standalone)').matches
    }
    setIsInstalled(check())

    const mq = window.matchMedia('(display-mode: standalone)')
    const handler = () => setIsInstalled(mq.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  return (
    <section className="rounded-2xl border border-border-subtle bg-bg-elevated/40 p-6 space-y-4">
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-full bg-accent/15 text-accent flex items-center justify-center shrink-0">
          <Download className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-medium">App installieren (PWA)</h3>
          <p className="text-sm text-text-tertiary mt-1">
            Als installierte App erhältst du Push-Benachrichtigungen und ein App-Icon auf deinem
            Gerät.
          </p>
        </div>
      </div>

      {isInstalled ? (
        <div className="rounded-lg border border-state-success/30 bg-state-success/5 px-4 py-2.5 text-sm text-state-success flex items-center gap-2">
          <Check className="h-4 w-4 shrink-0" />
          Bereits als App installiert
        </div>
      ) : (
        <div className="space-y-2">
          <CollapsibleInstructions title="iPhone / iPad">
            <ol className="list-decimal list-inside space-y-0.5">
              <li>Safari öffnen und diese Seite aufrufen</li>
              <li>Teilen-Button tippen (Quadrat mit Pfeil)</li>
              <li>&bdquo;Zum Home-Bildschirm&ldquo; auswählen</li>
              <li>&bdquo;Hinzufügen&ldquo; tippen</li>
            </ol>
            <p className="text-xs text-text-tertiary mt-1">
              Auf iPhone/iPad ist die Installation Voraussetzung für Push-Benachrichtigungen.
            </p>
          </CollapsibleInstructions>
          <CollapsibleInstructions title="Android">
            <ol className="list-decimal list-inside space-y-0.5">
              <li>Chrome öffnen und diese Seite aufrufen</li>
              <li>Menü (drei Punkte oben rechts) öffnen</li>
              <li>
                &bdquo;Zum Startbildschirm hinzufügen&ldquo; oder &bdquo;App installieren&ldquo;
                tippen
              </li>
            </ol>
          </CollapsibleInstructions>
          <CollapsibleInstructions title="Desktop (Chrome / Edge)">
            <ol className="list-decimal list-inside space-y-0.5">
              <li>
                <strong>Chrome:</strong> Menü &rarr; &bdquo;Installieren&hellip;&ldquo; oder
                Install-Icon in der Adressleiste
              </li>
              <li>
                <strong>Edge:</strong> Menü &rarr; Apps &rarr; &bdquo;Diese Seite als App
                installieren&ldquo;
              </li>
            </ol>
          </CollapsibleInstructions>
        </div>
      )}
    </section>
  )
}

/* ------------------------------------------------------------------ */
/*  Section 4: KI-Assistent (Admin-Link)                              */
/* ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ */
/*  Section: Fastbill (draft push from the invoice preview)           */
/* ------------------------------------------------------------------ */

function FastbillSection() {
  const queryClient = useQueryClient()
  const { data } = useQuery({
    queryKey: ['app-settings'],
    queryFn: () => api.getAppSettings(),
  })
  const settings = data?.settings
  const [email, setEmail] = useState('')
  const [projectId, setProjectId] = useState('')
  const [apiKey, setApiKey] = useState('')
  const [showKey, setShowKey] = useState(false)

  // Seed the editable fields from the server once loaded (the API key is
  // never returned — we only know whether one is stored).
  useEffect(() => {
    if (settings) {
      setEmail(settings.fastbillEmail ?? '')
      setProjectId(settings.fastbillProjectId ?? '')
    }
  }, [settings])

  const save = useMutation({
    mutationFn: () =>
      api.updateAppSettings({
        fastbillEmail: email.trim() || null,
        fastbillProjectId: projectId.trim() || null,
        // Only send the key when the admin typed a new one — an empty field
        // leaves the stored key untouched.
        ...(apiKey ? { fastbillApiKey: apiKey } : {}),
      }),
    onSuccess: () => {
      setApiKey('')
      queryClient.invalidateQueries({ queryKey: ['app-settings'] })
      notify.success('Fastbill-Einstellungen gespeichert')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const syncCustomers = useMutation({
    mutationFn: () => api.syncFastbillCustomers(),
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: ['customers'] })
      notify.success(
        `Fastbill-Sync: ${r.pulled} geprüft · ${r.created} neu angelegt · ${r.linked} verknüpft`,
      )
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const syncProjects = useMutation({
    mutationFn: () => api.syncFastbillProjects(),
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      notify.success(
        `Projekt-Sync: ${r.pulled} geprüft · ${r.created} neu angelegt · ${r.linked} verknüpft · ${r.skipped} übersprungen`,
      )
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const inputCls =
    'mt-1 w-full rounded-lg border border-border-default bg-bg-overlay/50 px-3 py-2 text-base lg:text-sm text-text-primary focus:border-accent/60 focus:outline-none focus:ring-2 focus:ring-accent/20'

  return (
    <section className="rounded-2xl border border-border-subtle bg-bg-elevated/40 p-6 space-y-4">
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-full bg-accent/15 text-accent flex items-center justify-center shrink-0">
          <Receipt className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-medium">Fastbill</h3>
          <p className="text-sm text-text-tertiary mt-1">
            Rechnungs-Vorschau direkt als Entwurf nach Fastbill pushen. Der API-Key wird
            verschlüsselt gespeichert (at-rest) und nie an den Browser zurückgegeben.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="text-sm text-text-secondary">
          Fastbill-Email
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputCls}
            placeholder="konto@firma.de"
            autoComplete="off"
          />
        </label>
        <label className="text-sm text-text-secondary">
          Projekt-ID
          <input
            type="text"
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            className={inputCls}
            placeholder="z. B. 12345"
            autoComplete="off"
          />
        </label>
        <label className="text-sm text-text-secondary sm:col-span-2">
          API-Key
          <div className="relative">
            <input
              type={showKey ? 'text' : 'password'}
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              className={`${inputCls} pr-11`}
              placeholder={
                settings?.fastbillApiKeySet ? '•••••••• (gespeichert)' : 'API-Key eintragen'
              }
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() => setShowKey((v) => !v)}
              className="absolute right-0 top-1 bottom-0 flex items-center px-3 text-text-tertiary hover:text-text-primary focus:outline-none focus:text-text-primary"
              aria-label={showKey ? 'API-Key verbergen' : 'API-Key anzeigen'}
              title={showKey ? 'Verbergen' : 'Anzeigen'}
            >
              {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </label>
      </div>

      <div className="flex items-center justify-between gap-3">
        <span className="text-xs text-text-tertiary">
          {settings?.fastbillApiKeySet
            ? 'API-Key hinterlegt. Feld leer lassen, um ihn zu behalten.'
            : 'Noch kein API-Key hinterlegt.'}
        </span>
        <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending}>
          {save.isPending ? 'Speichere …' : 'Speichern'}
        </Button>
      </div>

      {settings?.fastbillApiKeySet && (
        <div className="flex items-center justify-between gap-3 border-t border-border-subtle pt-4">
          <span className="text-xs text-text-tertiary">
            Kunden aus Fastbill importieren — legt fehlende lokal an und verknüpft bestehende (per
            USt-IdNr. oder exaktem Namen) ohne Duplikate.
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => syncCustomers.mutate()}
            disabled={syncCustomers.isPending}
            className="shrink-0 gap-2"
          >
            <RefreshCw className={`h-4 w-4${syncCustomers.isPending ? ' animate-spin' : ''}`} />
            {syncCustomers.isPending ? 'Synchronisiere …' : 'Kunden synchronisieren'}
          </Button>
        </div>
      )}

      {settings?.fastbillApiKeySet && (
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs text-text-tertiary">
            Projekte aus Fastbill importieren — ordnet sie dem lokalen Kunden zu (Kunden vorher
            synchronisieren); Projekte unbekannter Kunden werden übersprungen.
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => syncProjects.mutate()}
            disabled={syncProjects.isPending}
            className="shrink-0 gap-2"
          >
            <RefreshCw className={`h-4 w-4${syncProjects.isPending ? ' animate-spin' : ''}`} />
            {syncProjects.isPending ? 'Synchronisiere …' : 'Projekte synchronisieren'}
          </Button>
        </div>
      )}
    </section>
  )
}

function AiAssistantLink() {
  return (
    <section className="rounded-2xl border border-border-subtle bg-bg-elevated/40 p-6">
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-full bg-accent/15 text-accent flex items-center justify-center shrink-0">
          <Bot className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-medium">KI-Assistent</h3>
          <p className="text-sm text-text-tertiary mt-1">
            KI-Provider, Modell und System-Prompt konfigurieren.
          </p>
          <Link
            to="/settings/ai"
            className="mt-3 inline-flex items-center gap-2 text-sm text-accent hover:underline"
          >
            <Bot className="h-3.5 w-3.5" />
            KI-Assistent konfigurieren
          </Link>
        </div>
      </div>
    </section>
  )
}
